import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import {
  Building2, Shield, FileText, ChevronRight, Save, LogOut, Loader2, Pencil, X
} from "lucide-react";
import { useState, useEffect } from "react";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export default function EmpresaConfiguracoes() {
  const navigate = useNavigate();
  const { org, orgId, isLoading } = usePartnerContext();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    website: "",
    industry: "",
    city: "",
    state: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
  });

  useEffect(() => {
    if (org) {
      setForm({
        name: (org as any).name ?? "",
        website: (org as any).website ?? "",
        industry: (org as any).industry ?? "",
        city: (org as any).city ?? "",
        state: (org as any).state ?? "",
        contact_name: (org as any).contact_name ?? "",
        contact_email: (org as any).contact_email ?? "",
        contact_phone: (org as any).contact_phone ?? "",
      });
    }
  }, [org]);

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("partner_orgs")
        .update({
          name: form.name,
          website: form.website,
          industry: form.industry,
          city: form.city,
          state: form.state,
          contact_name: form.contact_name,
          contact_email: form.contact_email,
          contact_phone: form.contact_phone,
        })
        .eq("id", orgId);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["partner_context"] });
      toast.success("Dados atualizados com sucesso!");
      setEditing(false);
    } catch {
      toast.error("Erro ao salvar alterações.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/empresa/login", { replace: true });
  };

  if (isLoading) {
    return <div className="page-container-desktop flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const partnerTypes = (org as any)?.partner_types as string[] | null;

  return (
    <div className="page-container-desktop">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">Configurações</h1>
        <p className="text-muted-foreground mt-1">Gerencie os dados da sua empresa</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Dados da Empresa */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl p-6 border border-border">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-heading text-lg font-semibold text-foreground">Dados da Empresa</h2>
              </div>
              {!editing && (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil className="w-4 h-4" />Editar
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nome da Empresa" value={form.name} field="name" editing={editing} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="Website" value={form.website} field="website" editing={editing} onChange={(v) => setForm({ ...form, website: v })} />
              <Field label="Segmento" value={form.industry} field="industry" editing={editing} onChange={(v) => setForm({ ...form, industry: v })} />
              <Field label="Cidade" value={form.city} field="city" editing={editing} onChange={(v) => setForm({ ...form, city: v })} />
              <Field label="Estado" value={form.state} field="state" editing={editing} onChange={(v) => setForm({ ...form, state: v })} />
              <Field label="Contato (Nome)" value={form.contact_name} field="contact_name" editing={editing} onChange={(v) => setForm({ ...form, contact_name: v })} />
              <Field label="E-mail de Contato" value={form.contact_email} field="contact_email" editing={editing} onChange={(v) => setForm({ ...form, contact_email: v })} />
              <Field label="Telefone de Contato" value={form.contact_phone} field="contact_phone" editing={editing} onChange={(v) => setForm({ ...form, contact_phone: v })} />
            </div>

            {partnerTypes && partnerTypes.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-foreground mb-2">Tipos de Parceria</p>
                <div className="flex flex-wrap gap-2">
                  {partnerTypes.map((t, i) => (
                    <span key={i} className="px-3 py-1 bg-secondary text-secondary-foreground rounded-lg text-xs font-medium">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {editing && (
              <div className="flex gap-3 mt-6">
                <Button variant="hero" onClick={handleSave} disabled={saving}>
                  <Save className="w-4 h-4" />{saving ? "Salvando..." : "Salvar Alterações"}
                </Button>
                <Button variant="outline" onClick={() => { setEditing(false); if (org) setForm({ name: (org as any).name ?? "", website: (org as any).website ?? "", industry: (org as any).industry ?? "", city: (org as any).city ?? "", state: (org as any).state ?? "", contact_name: (org as any).contact_name ?? "", contact_email: (org as any).contact_email ?? "", contact_phone: (org as any).contact_phone ?? "" }); }}>
                  <X className="w-4 h-4" />Cancelar
                </Button>
              </div>
            )}
          </motion.div>

          {/* Conta */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl p-6 border border-border">
            <h2 className="font-heading text-lg font-semibold text-foreground mb-4">Conta</h2>
            <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-xl mb-4">
              <div>
                <p className="text-sm font-medium text-foreground">Logado como</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/5" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />Sair do Portal
            </Button>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl p-4 border border-border">
            <h3 className="font-heading font-semibold text-foreground mb-4">Acesso Rápido</h3>
            <div className="space-y-2">
              <button onClick={() => navigate("/empresa/auditoria")}
                className="w-full flex items-center justify-between p-3 bg-secondary/50 rounded-xl hover:bg-secondary transition-colors">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-foreground">Auditoria de Acesso</span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
              <button onClick={() => navigate("/empresa/termos")}
                className="w-full flex items-center justify-between p-3 bg-secondary/50 rounded-xl hover:bg-secondary transition-colors">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Termos e Governança</span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
            className="bg-primary/5 rounded-xl p-4 border border-primary/10">
            <Shield className="w-6 h-6 text-primary mb-2" />
            <p className="text-sm font-medium text-foreground mb-1">Conformidade LGPD</p>
            <p className="text-xs text-muted-foreground">
              Todos os dados são tratados conforme a Lei Geral de Proteção de Dados. Acesse a Auditoria para visualizar logs de acesso.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, editing, onChange }: { label: string; value: string; field: string; editing: boolean; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-sm font-medium text-foreground mb-2 block">{label}</label>
      {editing ? (
        <Input value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <p className="text-sm text-muted-foreground py-2 px-3 bg-secondary/50 rounded-lg min-h-[40px]">
          {value || "—"}
        </p>
      )}
    </div>
  );
}
